import React, { useEffect, useState } from 'react';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';
import { getUsuarios, createUsuario, updateUsuario } from '../../api/usuarios';
import usePaginacao from '../../hooks/usePaginacao';
import Paginacao from '../../components/common/Paginacao';
import { getEmpresas } from '../../api/empresas';

const PERFIL_LABEL = { admin: 'Admin', fenix: 'Fenix', cliente: 'Cliente' };
const PERFIL_COLOR = { admin: 'danger', fenix: 'primary', cliente: 'secondary' };
const EMPTY_FORM = { email: '', senha: '', perfil: 'fenix', id_empresa: '' };

export default function Usuarios() {
  const { isAdmin } = useAuth();
  const [usuarios, setUsuarios] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [error, setError] = useState('');

  const load = async () => {
    const [u, e] = await Promise.all([getUsuarios(), getEmpresas()]);
    setUsuarios(u);
    setEmpresas(e);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const set = (f) => (e) => setForm({ ...form, [f]: e.target.value });

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await createUsuario({
        email: form.email,
        senha: form.senha,
        perfil: form.perfil,
        id_empresa: form.perfil === 'cliente' && form.id_empresa ? form.id_empresa : null,
      });
      setForm(EMPTY_FORM);
      setShowForm(false);
      load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao criar usuário');
    }
  };

  const startEdit = (u) => {
    setEditId(u.id_usuario_portal);
    setEditForm({ perfil: u.perfil, ativo: u.ativo, id_empresa: u.id_empresa || '' });
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      await updateUsuario(editId, { perfil: editForm.perfil, ativo: editForm.ativo });
      setEditId(null);
      load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao atualizar usuário');
    }
  };

  const handleToggleAtivo = async (u) => {
    const acao = u.ativo ? 'inativar' : 'reativar';
    if (!window.confirm(`Deseja ${acao} o usuário ${u.email}?`)) return;
    try {
      await updateUsuario(u.id_usuario_portal, { ativo: !u.ativo });
      load();
    } catch (err) {
      setError(err.response?.data?.detail || `Erro ao ${acao} usuário`);
    }
  };

  const perfilOptions = isAdmin
    ? ['admin', 'fenix', 'cliente']
    : ['fenix', 'cliente'];

  const empresaNome = (id) => {
    const emp = empresas.find(e => e.id_empresa === id);
    return emp ? (emp.nome_fantasia || emp.razao_social) : '—';
  };

  const pag = usePaginacao(usuarios);

  if (loading) return <LoadingSpinner />;

  return (
    <>
      <PageHeader
        title="Usuários"
        subtitle="Gestão de acessos ao portal"
        action={
          <button className="btn-datafit" onClick={() => { setShowForm(!showForm); setEditId(null); }}>
            <i className="bi bi-person-plus me-2"></i>Novo usuário
          </button>
        }
      />

      {error && (
        <div className="alert alert-danger py-2 small mb-3">
          <i className="bi bi-exclamation-triangle me-2"></i>{error}
        </div>
      )}

      {showForm && (
        <div className="card-datafit p-4 mb-4">
          <div className="card-header-datafit mb-3" style={{ borderRadius: '0 12px 12px 0', display: 'inline-block' }}>
            Novo usuário
          </div>
          <form onSubmit={handleCreate}>
            <div className="row g-3">
              <div className="col-md-5">
                <div className="form-floating">
                  <input type="email" className="form-control" id="email" placeholder="email@exemplo.com"
                    value={form.email} onChange={set('email')} required />
                  <label htmlFor="email">E-mail</label>
                </div>
              </div>
              <div className="col-md-3">
                <div className="form-floating">
                  <input type="password" className="form-control" id="senha" placeholder="Senha"
                    value={form.senha} onChange={set('senha')} required minLength={6} />
                  <label htmlFor="senha">Senha</label>
                </div>
              </div>
              <div className="col-md-2">
                <div className="form-floating">
                  <select className="form-select" id="perfil" value={form.perfil} onChange={set('perfil')}>
                    {perfilOptions.map(p => <option key={p} value={p}>{PERFIL_LABEL[p]}</option>)}
                  </select>
                  <label htmlFor="perfil">Perfil</label>
                </div>
              </div>
              {form.perfil === 'cliente' && (
                <div className="col-md-2">
                  <div className="form-floating">
                    <select className="form-select" id="empresa" value={form.id_empresa} onChange={set('id_empresa')}>
                      <option value="">Sem empresa</option>
                      {empresas.map(e => (
                        <option key={e.id_empresa} value={e.id_empresa}>
                          {e.nome_fantasia || e.razao_social}
                        </option>
                      ))}
                    </select>
                    <label htmlFor="empresa">Empresa</label>
                  </div>
                </div>
              )}
            </div>
            <div className="d-flex gap-2 mt-3">
              <button type="submit" className="btn-datafit" style={{ height: 40, fontSize: '.85rem' }}>Salvar</button>
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setShowForm(false)}>Cancelar</button>
            </div>
          </form>
        </div>
      )}

      <div className="card-datafit">
        <div className="table-responsive">
          <table className="table table-datafit mb-0">
            <thead>
              <tr><th>E-mail</th><th>Perfil</th><th>Empresa</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              {usuarios.length === 0 ? (
                <tr><td colSpan={5} className="text-center text-muted py-4">Nenhum usuário encontrado</td></tr>
              ) : pag.paginados.map(u => (
                <React.Fragment key={u.id_usuario_portal}>
                  <tr>
                    <td>{u.email}</td>
                    <td>
                      <span className={`badge bg-${PERFIL_COLOR[u.perfil] || 'secondary'}`}>
                        {PERFIL_LABEL[u.perfil] || u.perfil}
                      </span>
                    </td>
                    <td className="text-muted small">{u.id_empresa ? empresaNome(u.id_empresa) : '—'}</td>
                    <td>
                      <span className={`badge ${u.ativo ? 'bg-success' : 'bg-secondary'}`}>
                        {u.ativo ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td>
                      <div className="d-flex gap-1">
                        <button className="btn btn-sm btn-outline-primary" title="Editar"
                          onClick={() => editId === u.id_usuario_portal ? setEditId(null) : startEdit(u)}>
                          <i className="bi bi-pencil"></i>
                        </button>
                        {isAdmin && (
                          <button
                            className={`btn btn-sm ${u.ativo ? 'btn-outline-danger' : 'btn-outline-success'}`}
                            title={u.ativo ? 'Inativar usuário' : 'Reativar usuário'}
                            onClick={() => handleToggleAtivo(u)}>
                            <i className={`bi ${u.ativo ? 'bi-person-slash' : 'bi-person-check'}`}></i>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {editId === u.id_usuario_portal && (
                    <tr key={`edit-${u.id_usuario_portal}`} className="table-light">
                      <td colSpan={5} className="p-3">
                        <form onSubmit={handleEdit} className="d-flex gap-3 flex-wrap align-items-end">
                          <div>
                            <label className="form-label small mb-1">Perfil</label>
                            <select className="form-select form-select-sm" style={{ width: 140 }}
                              value={editForm.perfil}
                              onChange={e => setEditForm({ ...editForm, perfil: e.target.value })}>
                              {perfilOptions.map(p => <option key={p} value={p}>{PERFIL_LABEL[p]}</option>)}
                            </select>
                          </div>
                          <div>
                            <label className="form-label small mb-1">Status</label>
                            <select className="form-select form-select-sm" style={{ width: 120 }}
                              value={editForm.ativo ? 'true' : 'false'}
                              onChange={e => setEditForm({ ...editForm, ativo: e.target.value === 'true' })}>
                              <option value="true">Ativo</option>
                              <option value="false">Inativo</option>
                            </select>
                          </div>
                          <button type="submit" className="btn btn-sm btn-primary">Salvar</button>
                          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setEditId(null)}>Cancelar</button>
                        </form>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        <Paginacao {...pag} />
      </div>
    </>
  );
}
